/**
 * The Simple and Advanced switch. Account level, global, URL addressable.
 *
 * ?adv=1 forces it on for the length of the visit, which is what makes "two
 * artboards of every screen" two URLs a reviewer can open side by side.
 * Advanced never removes, never reorders, never replaces. It appends.
 *
 * ONE SWITCH, TWO PLACES IT IS WRITTEN. The live value people read is
 * prefs.advanced, and the account record carries the same fact as
 * account.detailLevel, which is what an export, a server render or a second
 * client would read. Both switch surfaces in the product, the rail menu and
 * the account screen, go through this setter, so the write lives here rather
 * than in either of them: putting it in the two callers is how the two
 * eventually disagree. Before this, setDetailLevel had no callers at all and
 * account.detailLevel was frozen at 'simple' whatever a person picked, which
 * was a second account setting quietly contradicting the console.
 *
 * HIDDEN BY DEFAULT (2026-09-27). The switch is off for the Alpha launch and
 * comes back only with VITE_DETAIL_TOGGLE_ENABLED=true at build time. Off
 * means more than hiding the three controls: this hook then reports Simple
 * whatever prefs.advanced or ?adv=1 say, because a person who picked Advanced
 * before the switch was hidden would otherwise be stuck in it with no way
 * out. Their stored choice is left alone, so turning the flag back on
 * restores it.
 */

import { useCallback } from 'react';
import { useLocation } from 'react-router';
import { setDetailLevel } from '../api';
import { useDemoStore } from '../mock/store';

/** Whether the Simple and Advanced switch exists in this build. Off unless set. */
export const DETAIL_TOGGLE_ENABLED = import.meta.env.VITE_DETAIL_TOGGLE_ENABLED === 'true';

export function useAdvanced(): [boolean, (next: boolean) => void] {
  const location = useLocation();
  const stored = useDemoStore((s) => s.prefs.advanced);
  const setPref = useDemoStore((s) => s.setPref);

  const forced = new URLSearchParams(location.search).get('adv') === '1';

  const set = useCallback(
    (next: boolean) => {
      setPref('advanced', next);
      // The record follows the switch. Nothing awaits it: the console already
      // reads prefs.advanced, and a person should never watch a preference
      // land.
      void setDetailLevel(next ? 'advanced' : 'simple');
    },
    [setPref],
  );

  return [DETAIL_TOGGLE_ENABLED && (stored || forced), set];
}
